import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const AboutSection = styled.section`
  padding: 6rem 2rem;
  background-color: var(--dark-color);
  color: var(--light-color);
`;

const AboutContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 4rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const AboutContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 1.5rem;
  color: var(--accent-color);
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 60px;
    height: 3px;
    background-color: var(--secondary-color);
  }
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const AboutText = styled.p`
  font-size: 1.1rem;
  line-height: 1.8;
  margin-bottom: 2rem;
  opacity: 0.9;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const AboutFeatures = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  margin-top: 1rem;
`;

const Feature = styled(motion.div)`
  background-color: rgba(255, 255, 255, 0.05);
  padding: 1.5rem;
  border-radius: 10px;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    transform: translateY(-5px);
  }
`;

const FeatureTitle = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 0.5rem;
  color: var(--accent-color);
`;

const FeatureText = styled.p`
  font-size: 0.9rem;
  line-height: 1.6;
  opacity: 0.8;
`;

const AboutImage = styled(motion.div)`
  position: relative;
  height: 100%;
  min-height: 400px;
  border-radius: 10px;
  overflow: hidden;
  background-image: url('https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3');
  background-size: cover;
  background-position: center;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      to bottom,
      rgba(139, 115, 85, 0.3),
      rgba(44, 44, 44, 0.6)
    );
  }
  
  @media (max-width: 768px) {
    min-height: 300px;
  }
`;

const AboutImageOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  padding: 2rem;
  width: 100%;
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.8),
    rgba(0, 0, 0, 0)
  );
  
  h3 {
    font-size: 1.8rem;
    color: var(--accent-color);
    margin-bottom: 0.5rem;
  }
  
  p {
    font-size: 1rem;
    opacity: 0.8;
  }
`;

const About: React.FC = () => {
  return (
    <AboutSection id="about">
      <AboutContainer>
        <AboutContent>
          <SectionTitle>Hikayemiz</SectionTitle>
          <AboutText>
            Flinstones RestoCafe, aileniz ve sevdiklerinizle unutulmaz anlar yaşayabileceğiniz sıcak ve samimi bir restoran. Modern şehir hayatının yoğunluğunda, doğal ve organik malzemelerle hazırladığımız özel yemeklerimizle size evinizdeki konfor hissini yaşatıyoruz.
          </AboutText>
          <AboutText>
            Geleneksel lezzetleri çağdaş sunum teknikleriyle buluşturan menümüz, her yaştan misafirimize hitap ediyor. Özenle seçilmiş malzemeler, deneyimli şeflerimizin mahareti ve misafirperverliğimizle her öğünü özel kılıyoruz.
          </AboutText>
          
          <AboutFeatures>
            <Feature
              whileHover={{ y: -5 }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <FeatureTitle>Taze Malzemeler</FeatureTitle>
              <FeatureText>
                Günlük taze tedarik edilen organik malzemeler ile sağlıklı ve lezzetli yemekler.
              </FeatureText>
            </Feature>
            
            <Feature
              whileHover={{ y: -5 }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <FeatureTitle>Uzman Şefler</FeatureTitle>
              <FeatureText>
                Deneyimli şeflerimiz tarafından özenle hazırlanan özel tarifler ve sunumlar.
              </FeatureText>
            </Feature>
            
            <Feature
              whileHover={{ y: -5 }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <FeatureTitle>Premium Kahve</FeatureTitle>
              <FeatureText>
                Dünyanın en seçkin kahve bölgelerinden özel olarak seçilmiş çekirdekler.
              </FeatureText>
            </Feature>
            
            <Feature
              whileHover={{ y: -5 }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <FeatureTitle>Sıcak Atmosfer</FeatureTitle>
              <FeatureText>
                Samimi ve konforlu ortamda aileniz ve arkadaşlarınızla keyifli vakit geçirin.
              </FeatureText>
            </Feature>
          </AboutFeatures>
        </AboutContent>
        
        <AboutImage
          initial={{ opacity: 0, x: 50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <AboutImageOverlay>
            <h3>Flinstones RestoCafe</h3>
            <p>Sıcak atmosfer, özel lezzetler - ev konforunda restoran deneyimi</p>
          </AboutImageOverlay>
        </AboutImage>
      </AboutContainer>
    </AboutSection>
  );
};

export default About; 